// localStorage utility functions with comprehensive error handling

export interface LocalStorageResult<T = unknown> {
  success: boolean;
  data?: T | null;
  error?: string;
}

export interface StorageQuotaInfo {
  used: number;
  available: number;
  total: number;
  percentUsed: number;
}

/**
 * Safely set item in localStorage with error handling
 */
export function safeLocalStorageSetItem(key: string, value: string): LocalStorageResult {
  try {
    localStorage.setItem(key, value);
    return { success: true };
  } catch (error: unknown) {
    const err = error as Error;
    if (err.name === 'QuotaExceededError') {
      return { 
        success: false, 
        error: 'QUOTA_EXCEEDED',
      };
    }
    if (err.name === 'SecurityError') {
      return { 
        success: false, 
        error: 'STORAGE_DISABLED',
      };
    }
    return { 
      success: false, 
      error: 'STORAGE_ERROR',
    };
  }
}

/**
 * Safely get item from localStorage with error handling and validation
 */
export function safeLocalStorageGetItem<T = unknown>(key: string): LocalStorageResult<T> {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return { success: true, data: null as T | null };
    }
    
    const parsed = JSON.parse(item);
    return { success: true, data: parsed };
  } catch (error: unknown) {
    const err = error as Error;
    if (err.name === 'SecurityError') {
      return { 
        success: false, 
        error: 'STORAGE_DISABLED',
      };
    }
    // JSON parsing error - corrupted data
    return { 
      success: false, 
      error: 'CORRUPTED_DATA',
    };
  }
}

/**
 * Safely remove item from localStorage
 */
export function safeLocalStorageRemoveItem(key: string): LocalStorageResult {
  try {
    localStorage.removeItem(key);
    return { success: true };
  } catch (error: unknown) {
    const err = error as Error;
    if (err.name === 'SecurityError') {
      return { 
        success: false, 
        error: 'STORAGE_DISABLED',
      };
    }
    return { 
      success: false, 
      error: 'STORAGE_ERROR',
    };
  }
}

/**
 * Set object in localStorage with automatic JSON serialization and error handling
 */
export function safeSetObject<T>(key: string, obj: T): LocalStorageResult {
  try {
    const serialized = JSON.stringify(obj);
    return safeLocalStorageSetItem(key, serialized);
  } catch (error) {
    return { 
      success: false, 
      error: 'SERIALIZATION_ERROR',
    };
  }
}

/**
 * Get object from localStorage with automatic JSON parsing and validation
 */
export function safeGetObject<T>(key: string): LocalStorageResult<T> {
  return safeLocalStorageGetItem<T>(key);
}

/**
 * Check localStorage availability
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get storage quota information (approximate)
 * Note: This is an approximation as browsers don't expose exact quota info
 */
export function getStorageQuotaInfo(): StorageQuotaInfo {
  let used = 0;
  
  try {
    // Calculate approximate usage by measuring all localStorage items
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
  } catch {
    used = 0;
  }

  // Most browsers have 5-10MB limit, we'll use 5MB as conservative estimate
  const total = 5 * 1024 * 1024; // 5MB in bytes
  const available = total - used;
  const percentUsed = (used / total) * 100;

  return {
    used,
    available,
    total,
    percentUsed
  };
}

/**
 * Check if storage is approaching quota limit
 */
export function isStorageNearQuota(threshold: number = 80): boolean {
  const info = getStorageQuotaInfo();
  return info.percentUsed > threshold;
}

/**
 * Clear corrupted localStorage data with confirmation
 */
export function clearCorruptedData(keysToPreserve: string[] = []): LocalStorageResult {
  try {
    const itemsToPreserve: { [key: string]: string } = {};
    
    // Preserve important keys
    for (const key of keysToPreserve) {
      const item = localStorage.getItem(key);
      if (item) {
        itemsToPreserve[key] = item;
      }
    }
    
    // Clear all localStorage
    localStorage.clear();
    
    // Restore preserved items
    for (const [key, value] of Object.entries(itemsToPreserve)) {
      localStorage.setItem(key, value);
    }
    
    return { success: true };
  } catch {
    return { 
      success: false, 
      error: 'CLEAR_FAILED' 
    };
  }
}

/**
 * Trip data interface for validation
 */
interface TripData {
  _id: string;
  userId: string;
  status: string;
  messages?: unknown[];
  activities?: unknown[];
  [key: string]: unknown;
}

/**
 * Validate trip data structure
 */
export function validateTripData(data: unknown): data is TripData {
  if (!data || typeof data !== 'object' || data === null) return false;
  
  const obj = data as Record<string, unknown>;
  
  // Required fields
  if (typeof obj._id !== 'string') return false;
  if (typeof obj.userId !== 'string') return false;
  if (typeof obj.status !== 'string') return false;
  
  // Messages should be an array
  if (obj.messages && !Array.isArray(obj.messages)) return false;
  
  // Activities should be an array
  if (obj.activities && !Array.isArray(obj.activities)) return false;
  
  return true;
}

/**
 * Development utility - clear all trip data (for testing)
 */
export function clearAllTripData(): LocalStorageResult {
  try {
    localStorage.removeItem('currentTrip');
    localStorage.removeItem('user');
    return { success: true };
  } catch {
    return { 
      success: false, 
      error: 'CLEAR_FAILED' 
    };
  }
}

/**
 * Get human-readable error messages
 */
export function getErrorMessage(error: string): string {
  const messages: { [key: string]: string } = {
    QUOTA_EXCEEDED: 'Cannot save trip - storage is full. Please clear some browser data.',
    STORAGE_DISABLED: 'Local storage is disabled. Trip data will not be saved.',
    STORAGE_ERROR: 'Failed to save trip data. Please try again.',
    CORRUPTED_DATA: 'Saved trip data is corrupted. Starting fresh.',
    SERIALIZATION_ERROR: 'Failed to prepare trip data for saving.',
    CLEAR_FAILED: 'Failed to clear storage data.',
  };
  
  return messages[error] || 'An unknown storage error occurred.';
}

// Performance optimization utilities
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function monitorStorageUsage(): {
  isNearQuota: boolean;
  usagePercent: number;
  recommendation?: string;
} {
  const quota = getStorageQuotaInfo();
  const isNearQuota = quota.percentUsed > 85;
  
  return {
    isNearQuota,
    usagePercent: quota.percentUsed,
    recommendation: isNearQuota 
      ? 'Consider clearing old trip data to free up space'
      : undefined
  };
}